import React, { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";

import {
  User,
  Mail,
  Phone,
  Briefcase,
  Building2,
  CalendarDays,
  Shield,
  Hash,
  Loader2,
} from "lucide-react";

import api from "../../services/api_client";

import Navbar from "../../components/layout/Navbar";
import Sidebar from "../../components/layout/Sidebar";

import "./MonProfil.css";

export default function MonProfil() {
  const { user } = useAuth();

  const [profil, setProfil] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* =========================================================
     ROLE UTILISATEUR
  ========================================================= */

  const currentRole = String(
    profil?.role || user?.role || ""
  ).toUpperCase();

  const isRH = currentRole === "RH";
  const isManager = currentRole === "MANAGER";
  const isEmployee = currentRole === "EMPLOYEE";

  /*
   * RH :
   *    pas de Navbar
   *    pas de Sidebar
   *
   * MANAGER / EMPLOYEE :
   *    Navbar + Sidebar
   */

  const showLayout = !isRH;

  /* =========================================================
     CHARGEMENT DU PROFIL
  ========================================================= */

  useEffect(() => {
    const fetchProfil = async () => {
      try {
        setLoading(true);
        setError(null);

        /*
         * On utilise /auth/me car
         * /mon-espace/profil retourne actuellement 404.
         */
        const response = await api.get("/auth/me");

        console.log("[PROFIL] Données utilisateur :", response.data);

        const data = response.data || {};

        /*
         * Le backend peut retourner les informations
         * sous différents noms.
         */
        const employee =
          data.employee ||
          data.employe ||
          data.employee_data ||
          {};

        const profilNormalise = {
          id:
            data.id ??
            employee.id ??
            null,

          matricule:
            data.matricule ??
            employee.matricule ??
            data.employee_id ??
            employee.employee_id ??
            "Non renseigné",

          nom:
            data.last_name ??
            data.lastname ??
            data.nom ??
            employee.last_name ??
            employee.lastname ??
            employee.nom ??
            "",

          prenom:
            data.first_name ??
            data.firstname ??
            data.prenom ??
            employee.first_name ??
            employee.firstname ??
            employee.prenom ??
            data.name ??
            "",

          email:
            data.email ??
            employee.email ??
            user?.email ??
            "",

          telephone:
            data.phone ??
            data.telephone ??
            employee.phone ??
            employee.telephone ??
            "",

          departement:
            data.department ??
            data.departement ??
            employee.department ??
            employee.departement ??
            "",

          poste:
            data.job_title ??
            data.poste ??
            employee.job_title ??
            employee.poste ??
            "",

          date_naissance:
            data.birth_date ??
            data.date_naissance ??
            employee.birth_date ??
            employee.date_naissance ??
            null,

          date_embauche:
            data.hire_date ??
            data.date_embauche ??
            employee.hire_date ??
            employee.date_embauche ??
            null,

          role:
            data.role ??
            employee.role ??
            user?.role ??
            "",

          is_active:
            data.is_active ??
            employee.is_active ??
            true,
        };

        /*
         * Si le backend ne retourne pas prénom/nom,
         * on récupère le nom depuis AuthContext.
         */
        if (
          !profilNormalise.prenom &&
          !profilNormalise.nom &&
          user?.name
        ) {
          const parts = user.name.trim().split(" ");

          profilNormalise.prenom = parts.shift() || "";
          profilNormalise.nom = parts.join(" ") || "";
        }

        setProfil(profilNormalise);

      } catch (err) {
        console.error("[PROFIL] Erreur :", err);

        setError("Impossible de charger votre profil.");

        /*
         * Même si l'API échoue, on utilise les informations
         * disponibles dans AuthContext.
         */
        if (user) {
          setProfil({
            id: user.id,

            matricule: "Non renseigné",

            nom:
              user.last_name ||
              user.lastname ||
              "",

            prenom:
              user.first_name ||
              user.firstname ||
              user.name ||
              "",

            email:
              user.email ||
              "",

            telephone:
              user.phone ||
              "",

            departement:
              user.department ||
              "",

            poste:
              user.job_title ||
              "",

            date_naissance: null,

            date_embauche: null,

            role:
              user.role ||
              "",

            is_active:
              user.is_active ??
              true,
          });
        }

      } finally {
        setLoading(false);
      }
    };

    fetchProfil();

  }, [user]);

  /* =========================================================
     FORMAT DATE
  ========================================================= */

  const formatDate = (date) => {
    if (!date) {
      return "Non définie";
    }

    try {
      return new Date(date).toLocaleDateString(
        "fr-FR",
        {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        }
      );
    } catch {
      return "Non définie";
    }
  };

  /* =========================================================
     ROLE LABEL
  ========================================================= */

  const getRoleLabel = (role) => {
    switch (String(role).toUpperCase()) {
      case "RH":
        return "Ressources Humaines";

      case "EMPLOYEE":
        return "Employé";

      case "MANAGER":
        return "Manager";

      case "ADMIN":
        return "Administrateur";

      default:
        return role || "Utilisateur";
    }
  };

  /* =========================================================
     INITIALS
  ========================================================= */

  const getInitials = () => {
    const first =
      profil?.prenom?.charAt(0) || "";

    const last =
      profil?.nom?.charAt(0) || "";

    const initials =
      `${first}${last}`.toUpperCase();

    if (initials) {
      return initials;
    }

    return (
      user?.name?.charAt(0)?.toUpperCase() ||
      "U"
    );
  };

  /* =========================================================
     LOADING
  ========================================================= */

  if (loading) {
    return (
      <div
        className={`app-container ${
          isRH ? "profil-rh-page" : ""
        }`}
      >

        {/* Navbar uniquement pour Manager / Employee */}
        {showLayout && <Navbar />}

        <div
          className={
            showLayout
              ? "dashboard-content-wrapper"
              : "profil-rh-content"
          }
        >

          {/* Sidebar uniquement pour Manager / Employee */}
          {showLayout && <Sidebar />}

          <main className="main-content">

            <div className="profil-loading">

              <Loader2
                size={36}
                className="profil-spinner"
              />

              <p>
                Chargement de votre profil...
              </p>

            </div>

          </main>

        </div>

      </div>
    );
  }

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <div
      className={`app-container ${
        isRH ? "profil-rh-page" : ""
      }`}
    >

      {/* =====================================================
          NAVBAR
          Seulement Manager et Employee
      ===================================================== */}

      {showLayout && <Navbar />}

      <div
        className={
          showLayout
            ? "dashboard-content-wrapper"
            : "profil-rh-content"
        }
      >

        {/* =====================================================
            SIDEBAR
            Seulement Manager et Employee
        ===================================================== */}

        {showLayout && <Sidebar />}

        {/* =====================================================
            CONTENU PROFIL
        ===================================================== */}

        <main className="main-content">

          <div className="profil-page">

            {/* =================================================
                HEADER
            ================================================= */}

            <div className="profil-page-header">

              <div>

                <h1>
                  Mon profil
                </h1>

                <p>
                  Consultez vos informations personnelles
                  et professionnelles.
                </p>

              </div>

            </div>

            {/* =================================================
                ERREUR
            ================================================= */}

            {error && (
              <div className="profil-error">

                <span>
                  {error}
                </span>

              </div>
            )}

            {/* =================================================
                CARTE PROFIL
            ================================================= */}

            <section className="profil-hero">

              <div className="profil-avatar-large">
                {getInitials()}
              </div>

              <div className="profil-hero-info">

                <h2>
                  {profil?.prenom || "Utilisateur"}{" "}
                  {profil?.nom || ""}
                </h2>

                <p>
                  {profil?.poste ||
                    getRoleLabel(profil?.role)}
                </p>

                <div className="profil-hero-meta">

                  <span>

                    <Building2 size={15} />

                    {profil?.departement ||
                      "Département non défini"}

                  </span>

                  <span>

                    <Shield size={15} />

                    {getRoleLabel(
                      profil?.role
                    )}

                  </span>

                </div>

              </div>

              <div
                className={`profil-status ${
                  profil?.is_active
                    ? "active"
                    : "inactive"
                }`}
              >

                <span className="status-dot"></span>

                {profil?.is_active
                  ? "Compte actif"
                  : "Compte inactif"}

              </div>

            </section>

            {/* =================================================
                INFORMATIONS PERSONNELLES
            ================================================= */}

            <section className="profil-section">

              <div className="profil-section-header">

                <div className="profil-section-icon">

                  <User size={20} />

                </div>

                <div>

                  <h3>
                    Informations personnelles
                  </h3>

                  <p>
                    Vos informations d'identité
                  </p>

                </div>

              </div>

              <div className="profil-info-grid">

                {/* Prénom */}

                <div className="profil-info-item">

                  <label>
                    Prénom
                  </label>

                  <div className="profil-info-value">

                    <User size={16} />

                    <span>
                      {profil?.prenom ||
                        "Non renseigné"}
                    </span>

                  </div>

                </div>

                {/* Nom */}

                <div className="profil-info-item">

                  <label>
                    Nom
                  </label>

                  <div className="profil-info-value">

                    <User size={16} />

                    <span>
                      {profil?.nom ||
                        "Non renseigné"}
                    </span>

                  </div>

                </div>

                {/* Date naissance */}

                <div className="profil-info-item">

                  <label>
                    Date de naissance
                  </label>

                  <div className="profil-info-value">

                    <CalendarDays size={16} />

                    <span>
                      {formatDate(
                        profil?.date_naissance
                      )}
                    </span>

                  </div>

                </div>

                {/* Matricule */}

                <div className="profil-info-item">

                  <label>
                    Matricule
                  </label>

                  <div className="profil-info-value">

                    <Hash size={16} />

                    <span>
                      {profil?.matricule ||
                        "Non renseigné"}
                    </span>

                  </div>

                </div>

              </div>

            </section>

            {/* =================================================
                COORDONNEES
            ================================================= */}

            <section className="profil-section">

              <div className="profil-section-header">

                <div className="profil-section-icon">

                  <Mail size={20} />

                </div>

                <div>

                  <h3>
                    Coordonnées
                  </h3>

                  <p>
                    Vos coordonnées professionnelles
                  </p>

                </div>

              </div>

              <div className="profil-info-grid">

                {/* Email */}

                <div className="profil-info-item">

                  <label>
                    Adresse e-mail
                  </label>

                  <div className="profil-info-value">

                    <Mail size={16} />

                    <span>
                      {profil?.email ||
                        "Non renseigné"}
                    </span>

                  </div>

                </div>

                {/* Téléphone */}

                <div className="profil-info-item">

                  <label>
                    Téléphone
                  </label>

                  <div className="profil-info-value">

                    <Phone size={16} />

                    <span>
                      {profil?.telephone ||
                        "Non renseigné"}
                    </span>

                  </div>

                </div>

              </div>

            </section>

            {/* =================================================
                INFORMATIONS PROFESSIONNELLES
            ================================================= */}

            <section className="profil-section">

              <div className="profil-section-header">

                <div className="profil-section-icon">

                  <Briefcase size={20} />

                </div>

                <div>

                  <h3>
                    Informations professionnelles
                  </h3>

                  <p>
                    Votre situation dans l'entreprise
                  </p>

                </div>

              </div>

              <div className="profil-info-grid">

                {/* Département */}

                <div className="profil-info-item">

                  <label>
                    Département
                  </label>

                  <div className="profil-info-value">

                    <Building2 size={16} />

                    <span>
                      {profil?.departement ||
                        "Non défini"}
                    </span>

                  </div>

                </div>

                {/* Poste */}

                <div className="profil-info-item">

                  <label>
                    Poste
                  </label>

                  <div className="profil-info-value">

                    <Briefcase size={16} />

                    <span>
                      {profil?.poste ||
                        "Non défini"}
                    </span>

                  </div>

                </div>

                {/* Date embauche */}

                <div className="profil-info-item">

                  <label>
                    Date d'embauche
                  </label>

                  <div className="profil-info-value">

                    <CalendarDays size={16} />

                    <span>
                      {formatDate(
                        profil?.date_embauche
                      )}
                    </span>

                  </div>

                </div>

                {/* Rôle */}

                <div className="profil-info-item">

                  <label>
                    Rôle
                  </label>

                  <div className="profil-info-value">

                    <Shield size={16} />

                    <span>
                      {getRoleLabel(
                        profil?.role
                      )}
                    </span>

                  </div>

                </div>

              </div>

            </section>

          </div>

        </main>

      </div>

    </div>
  );
}